// Incremental POST/SSE client for Kara/Lala chat. Keeps the legacy JSON endpoint as a
// compatibility fallback while making streamed MiniMax output visible immediately.
export function visibleChatText(raw) {
  const text = String(raw || "");
  if (!text) return "";
  try {
    const cleaned = text.trim().replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
    const parsed = JSON.parse(cleaned);
    return typeof parsed?.message === "string" ? parsed.message : text;
  } catch {
    const match = text.match(/"message"\s*:\s*"((?:\\.|[^"\\])*)/s);
    if (!match) return text;
    try { return JSON.parse(`"${match[1].replace(/"\s*,?\s*$/, "")}"`); }
    catch { return match[1].replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\"); }
  }
}

function parseSseBlock(block) {
  let event = "message";
  const data = [];
  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
  }
  if (!data.length) return null;
  let payload;
  try { payload = JSON.parse(data.join("\n")); }
  catch { payload = { text: data.join("\n") }; }
  return { event, payload };
}


// ── INSTANT LOCAL ACK (2026-09-25, Kenyon: "the chatbots should let the user know
// it's working on it immediately and then execute the tasks") ──
// Runs synchronously in the browser BEFORE any network call, so the user always gets
// acknowledged in the same frame they hit send — even if the server is slow or down.
const ED = "\u2014", EL = "\u2026";
const WB = "\\b";
export function instantAckText(prompt, aiName = "") {
  const t = String(prompt || "").trim();
  const l = t.toLowerCase();
  const who = aiName ? aiName + " " : "";
  // Priority: greeting -> money/PnL -> long-running (backtest) -> trade -> research ->
  // strategy/config -> scan -> voice -> help -> generic. Long-running checks come BEFORE
  // the keyword groups they would otherwise shadow (e.g. "backtest the URUS strategy").
  if (new RegExp("^(hi|hello|hey|yo|good (morning|evening|afternoon)|wagwan)" + WB).test(l)) return "Right here " + ED + " what do you need?";
  if (new RegExp(WB + "(pnl|profit|today|earnings|balance|made|lost|how much)" + WB).test(l)) return "On it " + ED + " pulling the numbers now" + EL;
  if (new RegExp(WB + "(backtest|monte carlo|montecarlo|wfa|walk forward|optimi[sz]e|optimization|tune)" + WB).test(l)) return "On it " + ED + " spinning up the backtest engine (this one takes a minute)" + EL;
  if (new RegExp(WB + "(trade|buy|sell|execute|open|close|stake|position|call|put)" + WB).test(l)) return "On it " + ED + " checking the setup before I touch anything" + EL;
  if (new RegExp(WB + "(research|news|why|explain|analyse|analyze|analysis|report|study|compare)" + WB).test(l)) return "On it " + ED + " researching that now" + EL;
  if (new RegExp(WB + "(strategy|basket|switch|activate|settings|config|params|symbol)" + WB).test(l)) return "On it " + ED + " auditing strategy parameters" + EL;
  if (new RegExp(WB + "(scan|signal|htf|trend|setup|orderblock|fvg|breakout|sweep|condition)" + WB).test(l)) return "On it " + ED + " scanning market structure and live setups" + EL;
  if (new RegExp(WB + "(voice|tts|speak|elevenlabs|piper|mic|microphone|listen)" + WB).test(l)) return "On it " + ED + " checking the voice pipeline" + EL;
  if (new RegExp(WB + "(help|how|features|commands)" + WB).test(l)) return "On it " + ED + " pulling up what I can do" + EL;
  return (who + "On it " + ED + " working on that now" + EL).trim();
}

export async function streamChatResponse(apiFetch, payload, handlers = {}) {
  const controller = handlers.controller || new AbortController();
  // Fire the acknowledgement to the UI *now* — synchronously, before the first byte of
  // network I/O. Zero latency by construction; also covers a dead/slow server.
  try {
    const lastUser = [...(payload?.messages || [])].reverse().find(m => m?.role === "user");
    const localAck = instantAckText(lastUser?.content || "", payload?.agent === "lala" ? "Lala" : "");
    handlers.onWorking?.(localAck);
  } catch { /* never let UI sugar break the request */ }
  const requestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(payload),
    signal: controller.signal,
  };
  let response = await apiFetch("/api/chat/stream", requestInit);
  if (response.status === 404 || response.status === 405) {
    response = await apiFetch("/api/chat", requestInit);
  }
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    let message = body;
    try { message = JSON.parse(body)?.error || body; } catch { /* text error */ }
    throw new Error(message || `HTTP ${response.status}`);
  }
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("text/event-stream") || !response.body) {
    const data = await response.json();
    const text = data?.choices?.[0]?.message?.content || "";
    handlers.onDelta?.(text, text);
    // 2026-10-03 (Hermes) — surface server-side tool execution metadata so the UI
    // can render an inline badge for any actions the LLM emitted & the server ran.
    const meta = {
      full_text: text,
      actions_executed: data?.actions_executed || [],
      pending_confirmations: data?.pending_confirmations || [],
      fallback: true,
    };
    handlers.onDone?.(meta);
    return Object.assign(new String(text), { __chatMeta: meta });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullText = "";
  let donePayload = null;
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() || "";
      for (const block of blocks) {
        const parsed = parseSseBlock(block);
        if (!parsed) continue;
        const { event, payload: eventPayload } = parsed;
        if (event === "ack") {
          handlers.onAck?.(eventPayload);
          if (eventPayload?.ack && !handlers.onAck) handlers.onWorking?.(eventPayload.ack);
        }
        else if (event === "delta") {
          const chunk = String(eventPayload?.text || "");
          fullText += chunk;
          handlers.onDelta?.(chunk, fullText);
        } else if (event === "done") {
          donePayload = eventPayload;
          // 2026-10-03 (Hermes) — server's done event now carries actions_executed +
          // pending_confirmations from the server-side tool dispatcher. Surface them
          // so the chat UI can render a "🔧 2 tools ran" badge and a confirm modal.
          const meta = {
            ...eventPayload,
            actions_executed: eventPayload?.actions_executed || [],
            pending_confirmations: eventPayload?.pending_confirmations || [],
          };
          handlers.onDone?.(meta);
        } else if (event === "tool_progress") {
          // 2026-10-03 (Hermes) — server may emit a live update while a long-running
          // tool (backtest, sub-agent) is in flight. Surface to the UI as it arrives.
          handlers.onToolProgress?.(eventPayload);
        } else if (event === "error") {
          // 2026-09-25 (Hermes): server emits { message, retryable, fallback_error } —
          // this read only `error`, so EVERY server-side failure showed the generic
          // "Streaming chat failed" and hid the real cause (and any partial reply).
          const error = new Error(eventPayload?.error || eventPayload?.message || "Streaming chat failed");
          error.retryable = eventPayload?.retryable !== false;
          error.payload = eventPayload || null;
          error.partial = fullText; // keep whatever the user already saw
          throw error;
        }
      }
      if (done) break;
    }
  } finally {
    reader.releaseLock();
  }
  // 2026-09-25 (Hermes): the server's done event carries `text`; this only read
  // `full_text`. Any provider that returns its answer without emitting deltas (e.g. a
  // non-streamed upstream or the new server-side fallback) produced an EMPTY reply and
  // the UI silently rendered "Done, <user>." instead of the answer.
  if (!donePayload && !fullText) throw new Error("Chat stream ended before a done event");
  const finalText = donePayload?.full_text ?? donePayload?.text ?? fullText;
  // 2026-10-03 (Hermes) — also return the tool-execution metadata via a boxed string.
  // The caller (AmberScanner.jsx sendToKara) can read `.chatMeta()` to render badges
  // and the confirmation modal for any GATED tools the server ran.
  const meta = {
    full_text: finalText,
    actions_executed: donePayload?.actions_executed || [],
    pending_confirmations: donePayload?.pending_confirmations || [],
  };
  const boxed = Object.assign(new String(finalText), {
    chatMeta: () => meta,
  });
  return boxed;
}
